import { TlfItemTable } from "components/common/tlfItemTable/TlfItemTable";
import { useAppSettings, useHierarchyContext } from "contexts/AppSettingsContext";
import { eHierarchyLevel } from "functions/api/enums";
import { loadHierarchyTlfs } from "functions/api/hierarchy/loadHierarchyTlfs";
import { buildTlfBundleExportRequest } from "functions/api/tlfBundle/buildTlfBundleExportRequest";
import { exportTlfBundle } from "functions/api/tlfBundle/exportTlfBundle";
import { TlfBundleTransferProgress } from "functions/api/tlfBundle/interface";
import { saveTlfBundle } from "functions/api/tlfBundle/saveTlfBundle";
import { TlfItemIndexReadDto } from "functions/api/tlfItem/TlfItemIndexReadDto";
import { useCallback, useEffect, useRef, useState } from "react";
import { Alert, Button, Form, Modal, ProgressBar, Spinner } from "react-bootstrap";

interface TlfItemGroup {
  id: string;
  name: string;
  items: TlfItemIndexReadDto[];
}

interface ExportTlfBundleModalProps {
  onClose: () => void;
}

/**
 * Select hierarchy TLF items and export them as a verified compressed bundle.
 *
 * @param props - Modal callbacks
 * @param props.onClose - Callback used to close the export workflow
 * @returns Export selection and progress dialog
 */
export const ExportTlfBundleModal = ({
  onClose,
}: ExportTlfBundleModalProps): React.ReactElement => {
  const appSettings = useAppSettings();
  const hierarchyContext = useHierarchyContext();
  const hierarchy = hierarchyContext?.hierarchy;
  const isStudy = hierarchy?.level === eHierarchyLevel.Study;
  const [groups, setGroups] = useState<TlfItemGroup[]>([]);
  const [selectedItemIds, setSelectedItemIds] = useState<string[]>([]);
  const [searchText, setSearchText] = useState<string>("");
  const [includeDescendants, setIncludeDescendants] = useState<boolean>(isStudy);
  const [includeComments, setIncludeComments] = useState<boolean>(false);
  const [exportOnlyReferencedMetadata, setExportOnlyReferencedMetadata] = useState<boolean>(true);
  const [ignoreRetiredAndDecommissioned, setIgnoreRetiredAndDecommissioned] =
    useState<boolean>(true);
  const [includeParentHierarchyArsMetadata, setIncludeParentHierarchyArsMetadata] =
    useState<boolean>(false);
  const [step, setStep] = useState<"select" | "download">("select");
  const [loadingItems, setLoadingItems] = useState<boolean>(true);
  const [loadingError, setLoadingError] = useState<string | null>(null);
  const [exporting, setExporting] = useState<boolean>(false);
  const [exportError, setExportError] = useState<string | null>(null);
  const [progress, setProgress] = useState<TlfBundleTransferProgress | null>(null);
  const activeController = useRef<AbortController | null>(null);

  const loadItems = useCallback(
    async (withDescendants: boolean) => {
      if (!hierarchy) {
        setLoadingError("The current hierarchy is unavailable");
        setLoadingItems(false);
        return;
      }

      activeController.current?.abort();
      const controller = new AbortController();
      activeController.current = controller;
      setLoadingItems(true);
      setLoadingError(null);
      setSelectedItemIds([]);

      const sources = [
        { id: hierarchy.id, name: hierarchy.name, parentTag: hierarchy.parentTag },
        ...(withDescendants && isStudy
          ? hierarchy.children
              .filter((child) => child.level === eHierarchyLevel.Report)
              .map((child) => ({ id: child.id, name: child.name, parentTag: child.parentTag }))
          : []),
      ];

      try {
        const loadedSources = await Promise.all(
          sources.map(async (source) => ({
            source,
            response: await loadHierarchyTlfs(
              appSettings.backEndUrl,
              { hierarchyId: source.id, parentTag: source.parentTag },
              controller,
            ),
          })),
        );

        if (controller.signal.aborted) return;

        const nextGroups: TlfItemGroup[] = [];
        for (const { source, response } of loadedSources) {
          if (!response.success) {
            setLoadingError(response.errorText);
            return;
          }
          nextGroups.push({
            id: source.id,
            name: source.name,
            items: response.data.filter((item) => item.primaryHierarchyId === source.id),
          });
        }

        const nonEmptyGroups = nextGroups.filter((group) => group.items.length > 0);
        setGroups(nonEmptyGroups);
        setSelectedItemIds([
          ...new Set(nonEmptyGroups.flatMap((group) => group.items.map((item) => item.id))),
        ]);
      } catch (error: unknown) {
        if (!controller.signal.aborted) {
          setLoadingError(error instanceof Error ? error.message : "Unable to load TLF items");
        }
      } finally {
        if (!controller.signal.aborted) setLoadingItems(false);
      }
    },
    [appSettings.backEndUrl, hierarchy, isStudy],
  );

  useEffect(() => {
    let active = true;
    void Promise.resolve().then(() => {
      /* istanbul ignore else */
      // Cleanup invalidates the scheduled load before the modal can use its result.
      if (active) return loadItems(isStudy);
    });
    return () => {
      active = false;
      activeController.current?.abort();
    };
  }, [isStudy, loadItems]);

  const allItems = groups.flatMap((group) => group.items);
  const allItemIds = [...new Set(allItems.map((item) => item.id))];
  const availableIds = new Set(allItemIds);
  const selectedIds = [...new Set(selectedItemIds.filter((id) => availableIds.has(id)))];
  const selectedCount = selectedIds.length;
  const search = searchText.trim().toLowerCase();

  /**
   * Select all available items or clear the current selection.
   *
   * @param selectAll - Whether every available item should be selected
   */
  const toggleAllItems = (selectAll: boolean) => {
    setSelectedItemIds(selectAll ? allItemIds : []);
  };

  /**
   * Update the descendant option and reload the matching hierarchy indexes.
   *
   * @param enabled - Whether child Report indexes should be included
   */
  const toggleDescendants = (enabled: boolean) => {
    setIncludeDescendants(enabled);
    void loadItems(enabled);
  };

  /**
   * Advance to export options when at least one TLF item is selected.
   */
  const goToDownload = () => {
    /* istanbul ignore else */
    if (selectedCount > 0) {
      setExportError(null);
      setStep("download");
    }
  };

  /**
   * Create and download the selected TLF bundle using the current export options.
   *
   * @returns A promise that resolves after the export attempt completes
   */
  const startExport = async () => {
    /* istanbul ignore else */
    if (hierarchy && selectedCount > 0) {
      const controller = new AbortController();
      activeController.current = controller;
      setExporting(true);
      setExportError(null);
      setProgress(null);

      const request = buildTlfBundleExportRequest({
        hierarchyId: hierarchy.id,
        items: allItems,
        selectedItemIds: selectedIds,
        includeComments,
        includeDescendants,
        includeParentHierarchyArsMetadata,
        exportOnlyReferencedMetadata,
        ignoreRetiredAndDecommissioned,
      });
      try {
        const response = await exportTlfBundle(
          appSettings.backEndUrl,
          request,
          controller,
          setProgress,
        );
        if (response.success) {
          saveTlfBundle(response.data, hierarchy.name);
          onClose();
        } else {
          setExportError(response.errorText);
        }
      } catch (error: unknown) {
        setExportError(error instanceof Error ? error.message : "Unable to export TLF bundle");
      } finally {
        setExporting(false);
        /* istanbul ignore else */
        // Export controls stay disabled while this request owns the controller.
        if (activeController.current === controller) activeController.current = null;
      }
    }
  };

  /**
   * Abort the active item load or export request and close the modal.
   */
  const cancelWorkflow = () => {
    activeController.current?.abort();
    onClose();
  };

  const content =
    step === "select" ? (
      <div
        className="d-grid gap-3"
        style={{ minWidth: 0 }}
      >
        {isStudy && (
          <section className="border rounded-2 p-3">
            <h3 className="h6 mb-2">Hierarchy scope</h3>
            <p className="text-muted mb-3">Choose whether to include report-level items.</p>
            <Form.Check
              id="tlf-bundle-include-descendants"
              type="checkbox"
              label="Include descendant reports"
              checked={includeDescendants}
              disabled={loadingItems}
              onChange={(event) => toggleDescendants(event.target.checked)}
            />
          </section>
        )}
        <section
          className="border rounded-2 p-3"
          style={{ minWidth: 0 }}
        >
          <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-3">
            <div>
              <h3 className="h6 mb-1">Select TLF items</h3>
              <span
                className="text-muted"
                aria-live="polite"
              >
                {selectedCount} of {allItemIds.length} selected
              </span>
            </div>
            <div className="d-flex gap-2">
              <Button
                variant="outline-secondary"
                size="sm"
                onClick={() => toggleAllItems(true)}
                disabled={loadingItems || selectedCount === allItemIds.length}
              >
                Select all
              </Button>
              <Button
                variant="outline-secondary"
                size="sm"
                onClick={() => toggleAllItems(false)}
                disabled={loadingItems || selectedCount === 0}
              >
                Deselect all
              </Button>
            </div>
          </div>
          <Form.Group className="mb-3">
            <Form.Label htmlFor="tlf-bundle-search">Search by name or AZSOL ID</Form.Label>
            <Form.Control
              id="tlf-bundle-search"
              type="search"
              value={searchText}
              onChange={(event) => setSearchText(event.target.value)}
            />
          </Form.Group>
          {loadingItems && (
            <Spinner
              animation="border"
              aria-label="Loading TLF items"
            />
          )}
          {loadingError && (
            <Alert
              variant="danger"
              role="alert"
            >
              {loadingError}
              <Button
                variant="link"
                onClick={() => void loadItems(includeDescendants)}
              >
                Try again
              </Button>
            </Alert>
          )}
          {!loadingItems && !loadingError && allItemIds.length === 0 && (
            <p className="mb-0">No TLF items found in this hierarchy.</p>
          )}
          {!loadingItems &&
            !loadingError &&
            groups.map((group) => {
              const groupIds = new Set(group.items.map((item) => item.id));
              const groupSelectedCount = group.items.filter((item) =>
                selectedIds.includes(item.id),
              ).length;
              const groupSelected =
                group.items.length > 0 && groupSelectedCount === group.items.length;
              const groupPartiallySelected = groupSelectedCount > 0 && !groupSelected;
              const visibleItems = group.items.filter((item) =>
                [
                  group.name,
                  item.azsolId,
                  item.additionalInformation,
                  item.tocLabel,
                  item.tocNumber,
                ].some((value) => value?.toLowerCase().includes(search)),
              );

              return (
                <section
                  key={group.id}
                  className="border-top pt-3 mt-3"
                >
                  {!(isStudy && includeDescendants) && <h4 className="h6 mb-2">{group.name}</h4>}
                  {isStudy && includeDescendants && (
                    <Form.Check
                      id={`tlf-bundle-report-${group.id}`}
                      type="checkbox"
                      label={group.name}
                      checked={groupSelected}
                      aria-checked={groupPartiallySelected ? "mixed" : groupSelected}
                      ref={(input) => {
                        if (input) input.indeterminate = groupPartiallySelected;
                      }}
                      onChange={() => {
                        setSelectedItemIds((current) => {
                          const withoutGroup = current.filter((id) => !groupIds.has(id));
                          return groupSelected ? withoutGroup : [...withoutGroup, ...groupIds];
                        });
                      }}
                    />
                  )}
                  <div
                    className="overflow-auto"
                    style={{ maxHeight: "28vh", width: "100%", maxWidth: "100%", minWidth: 0 }}
                  >
                    <TlfItemTable
                      id={`tlf-bundle-items-${group.id}`}
                      label={isStudy && includeDescendants ? undefined : group.name}
                      showExport
                      tableClassName="tlf-item-table-export"
                      selectedItems={selectedIds}
                      setSelectedItems={(updatedIds) => {
                        const visibleIds = new Set(visibleItems.map((item) => item.id));
                        setSelectedItemIds((current) => [
                          ...current.filter((id) => !visibleIds.has(id)),
                          ...updatedIds.filter((id) => groupIds.has(id) && visibleIds.has(id)),
                        ]);
                      }}
                      searchResults={visibleItems}
                      showSearchResults
                    />
                  </div>
                </section>
              );
            })}
        </section>
      </div>
    ) : (
      <div className="d-grid gap-3">
        <section className="border rounded-2 p-3">
          <h3 className="h6 mb-2">Selection summary</h3>
          <p className="mb-0">
            {selectedCount} items from{" "}
            <strong>
              {hierarchy?.name ??
                // istanbul ignore next -- The download step requires a loaded hierarchy.
                "this hierarchy"}
            </strong>
          </p>
        </section>
        <section className="border rounded-2 p-3">
          <h3 className="h6 mb-2">Bundle options</h3>
          <div className="d-grid gap-3">
            <Form.Check
              id="tlf-bundle-include-comments"
              type="checkbox"
              label="Include comments"
              checked={includeComments}
              onChange={(event) => setIncludeComments(event.target.checked)}
            />
            <Form.Check
              id="tlf-bundle-export-only-referenced-metadata"
              type="checkbox"
              label="Export only referenced ARS metadata"
              checked={exportOnlyReferencedMetadata}
              onChange={(event) => setExportOnlyReferencedMetadata(event.target.checked)}
            />
            <Form.Check
              id="tlf-bundle-ignore-retired-and-decommissioned"
              type="checkbox"
              label="Ignore retired and decommissioned items"
              checked={ignoreRetiredAndDecommissioned}
              onChange={(event) => setIgnoreRetiredAndDecommissioned(event.target.checked)}
            />
            <Form.Check
              id="tlf-bundle-include-parent-hierarchy-ars-metadata"
              type="checkbox"
              label="Include parent hierarchy ARS metadata"
              checked={includeParentHierarchyArsMetadata}
              onChange={(event) => setIncludeParentHierarchyArsMetadata(event.target.checked)}
            />
          </div>
        </section>
        {exportError && (
          <Alert
            variant="danger"
            role="alert"
          >
            {exportError}
          </Alert>
        )}
        {progress && (
          <section
            className="border rounded-2 p-3"
            aria-live="polite"
          >
            <h3 className="h6 mb-2">Download progress</h3>
            <p>
              Downloaded {progress.transferredBytes.toLocaleString()} of{" "}
              {progress.totalBytes.toLocaleString()} bytes
            </p>
            <ProgressBar
              now={
                progress.totalBytes === 0
                  ? 0
                  : (progress.transferredBytes / progress.totalBytes) * 100
              }
              aria-label="Export progress"
            />
          </section>
        )}
      </div>
    );

  return (
    <Modal
      show
      size="xl"
      onHide={cancelWorkflow}
      aria-labelledby="tlf-bundle-export-title"
      centered
    >
      <Modal.Header closeButton>
        <Modal.Title id="tlf-bundle-export-title">Export TLF bundle</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <h2 className="h5">{step === "select" ? "Select items" : "Download"}</h2>
        {content}
      </Modal.Body>
      <Modal.Footer>
        <Button
          variant="secondary"
          onClick={cancelWorkflow}
        >
          Cancel
        </Button>
        {step === "download" && (
          <Button
            variant="secondary"
            onClick={() => setStep("select")}
            disabled={exporting}
          >
            Back to item selection
          </Button>
        )}
        {step === "select" ? (
          <Button
            variant="primary"
            onClick={goToDownload}
            disabled={loadingItems || Boolean(loadingError) || selectedCount === 0}
          >
            Next
          </Button>
        ) : (
          <Button
            variant="primary"
            onClick={() => void startExport()}
            disabled={exporting || selectedCount === 0}
          >
            {exporting ? "Exporting" : "Export TLF bundle"}
          </Button>
        )}
        {exporting && (
          <Button
            variant="outline-danger"
            onClick={() => activeController.current?.abort()}
          >
            Cancel download
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  );
};

ExportTlfBundleModal.displayName = "ExportTlfBundleModal";
